import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import { idZodSchema } from "@@/shared/id";
import { percentageZodSchema } from "@@/shared/percentage";
import { providerIdZodSchema } from "@@/shared/providers/provider-id";
import {
    requiredTranslatableZodSchema,
    translatableStringFactory,
} from "@@/shared/translations/translation";
import { generateId } from "@@/utilities/generate-id";
import { z } from "zod";
import { discountConditionZodSchema } from "../../conditions/discount-condition";
import { discountCodeZodString } from "../discount-code-string";
import { discountCodeUsageLimitZodObject } from "../discount-code-usage-limits";

const discountCodeCreateCommandZodObject = z.object({
    _id: idZodSchema.default(() =>
        generateId({ prefix: "discount_code_create_command__" }),
    ),
    _type: z
        .literal("DISCOUNT_CODE_CREATE_COMMAND")
        .default("DISCOUNT_CODE_CREATE_COMMAND"),
    _createdAt: isoAndUnixZodSchema.default(() => nowIsoAndUnix()),
    providerId: providerIdZodSchema,
    conditions: z.array(discountConditionZodSchema),
    value: percentageZodSchema.or(z.number().min(1)),
    title: requiredTranslatableZodSchema,
    type: z.enum(["PERCENTAGE", "VALUE"]),
    code: discountCodeZodString,
    usage: discountCodeUsageLimitZodObject,
    start: isoAndUnixZodSchema,
    end: isoAndUnixZodSchema,
});

const percentageCheck = (value: {
    type: "PERCENTAGE" | "VALUE";
    value: number;
}) => {
    if (value.type === "PERCENTAGE")
        return value.value >= 0 && value.value <= 1;
    return true;
};
const percentageRefineCheckErrorMessage = translatableStringFactory({
    sv: "När rabattkoden ska ge en procentuell rabatt måste rabatten vara ett procentuellt värde mellan 0 och 100%",
    en: "When the discount code should give a percentage discount, the discount must be a percentage value between 0 and 100%",
});
const valueCheck = (value: { type: "PERCENTAGE" | "VALUE"; value: number }) => {
    if (value.type === "VALUE") return value.value >= 0;
    return true;
};

const valueRefineCheckErrorMessage = translatableStringFactory({
    sv: "När rabattkoden är satt att vara en rabatt till ett fast värde måste rabatten vara ett positivt nummer",
    en: "When the discount code is set to be a discount to a fixed value, the discount must be a positive number",
});

type DiscountCodeCreateCommand = z.infer<
    typeof discountCodeCreateCommandZodObject
>;

const discountCodeCreateCommandFactoryParamsZodObject =
    discountCodeCreateCommandZodObject.omit({
        _id: true,
        _type: true,
        _createdAt: true,
    });

type FactoryParams = z.infer<
    typeof discountCodeCreateCommandFactoryParamsZodObject
>;

const discountCodeCreateCommandFactory = (
    params: FactoryParams,
): DiscountCodeCreateCommand => {
    return discountCodeCreateCommandZodObject.parse(params);
};

const discountCodeCreateCommandRefinements = {
    percentageCheck,
    valueCheck,
    percentageRefineCheckErrorMessage,
    valueRefineCheckErrorMessage,
};

export {
    discountCodeCreateCommandFactory,
    discountCodeCreateCommandFactoryParamsZodObject,
    discountCodeCreateCommandRefinements,
    discountCodeCreateCommandZodObject,
    percentageRefineCheckErrorMessage,
};
export type {
    DiscountCodeCreateCommand,
    FactoryParams as DiscountCodeCreateCommandFactoryParams,
};
